import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  LiveChatVisitorList,
  StartProactiveConversationRequest,
} from '@nucleus-commerce/api-client/admin';

import { conversationsApi } from '../api/conversations-api';
import { applyConversationToVisitors } from '../lib/visitors-cache';

import { conversationKeys } from './conversation-keys';

/**
 * "Start chat" from the visitor list: a pending conversation assigned to me with my message
 * first. The dialog renders 409 (already chatting) and 403 VISITOR_BANNED itself.
 */
export function useStartChat(storeId: string, visitorId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StartProactiveConversationRequest) =>
      conversationsApi.startChat(storeId, visitorId, body),
    meta: { silentError: true, successMessage: 'conversations:toasts.chatStarted' },
    onSuccess: ({ conversation }) => {
      queryClient.setQueryData(conversationKeys.detail(storeId, conversation.id), conversation);
      queryClient.setQueryData<LiveChatVisitorList>(conversationKeys.visitors(storeId), (list) =>
        applyConversationToVisitors(list, {
          id: conversation.id,
          number: conversation.number,
          status: conversation.status,
          assignedTo: conversation.assignee?.id ?? null,
          visitorId,
        }),
      );
      void queryClient.invalidateQueries({ queryKey: conversationKeys.counts() });
      return queryClient.invalidateQueries({ queryKey: conversationKeys.lists() });
    },
  });
}
